import { omit } from 'lodash';
import qs from 'qs';
import React, { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as Arrow } from 'src/assets/images/arrow.svg';
import { ReactComponent as Cross } from 'src/assets/images/encircled-cross.svg';
import { ReactComponent as NonVmiSite } from 'src/assets/images/non-vmi-site.svg';
import { ReactComponent as SMHIcon } from 'src/assets/images/smh-icon.svg';
import { ReactComponent as VmiSite } from 'src/assets/images/vmi-site.svg';
import { truncate } from 'src/components/truncate/truncate';
import styled, { css } from 'styled-components';
import { SHIP_TO_ID_PARAM } from '../constants/routes';
import { IShipTo, ISoldTo } from '../graphql/types';
import { Black, White2 } from '../styles/colours';
import { DemiBold } from '../styles/fonts';
import { CustomerType, CustomerTypes } from './customer-type/customer-type';

const StyledCustomerTile = styled.section`
  display: flex;
  flex-direction: column;
  width: 24rem;
`;

const tile = css`
  border-radius: 4px;
  background: ${White2};
  color: ${Black};
  padding: 1rem;
  margin-bottom: 0.25rem;
`;

const TileRow = styled.div`
  ${tile};
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TileColumn = styled.div`
  ${tile};
  display: flex;
  flex-direction: column;
`;

const CustomerName = styled.div`
  font-family: ${DemiBold};
  ${truncate};
`;

const CustomerId = styled.div`
  display: inline-block;
  padding: 0 1rem;
`;

const CustomerTilesContainer = styled.section`
  display: flex;
  align-items: center;
`;

const ArrowContainer = styled.div`
  margin: 0 1rem;
`;

const RemoveShipToLink = styled(Link)`
  margin-left: auto;
  height: 25px;
`;

const NameAndIconContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const SMHIconContainer = styled.div`
  margin-left: 7rem;
`;

const VmiIconContainer = styled.div`
  margin-left: 7rem;
`;

const CompanyAddress: FC<{ customer: ISoldTo | IShipTo }> = ({ customer }) => {
  const { address, salesOrg } = customer;
  return (
    <div data-type="company-address">
      <div data-type="address-street" title="Street">
        {address.street}
      </div>
      <div data-type="address-city" title="City">
        {address.city}
      </div>
      <div data-type="address-postcode" title="Post Code">
        {address.postalCode}
      </div>
      <div data-type="address-country" title="Country">
        {address.country}
      </div>
      {salesOrg && <div title="Sales Org">{salesOrg}</div>}
    </div>
  );
};

// displaySMHLabel = display shell market hub label

const CustomerTile: FC<{
  type: CustomerTypes;
  customer: ISoldTo | IShipTo;
  showRemoveLink?: boolean;
  displaySMHLabel?: boolean;
  isVmiSite?: string;
}> = ({
  showRemoveLink = false,
  customer,
  type,
  displaySMHLabel = false,
  isVmiSite,
}) => {
  const location = useLocation();
  const originalParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const params: any = omit(originalParams, SHIP_TO_ID_PARAM);

  return (
    <StyledCustomerTile data-type={`${type.toLowerCase()}-customer`}>
      <TileRow>
        <CustomerType type={type} />
        <CustomerId>
          ID: &nbsp;
          <span data-type="customer-id">{customer.id}</span>
        </CustomerId>
        {showRemoveLink && (
          <RemoveShipToLink
            data-type="sold-to-link"
            to={`${location.pathname}?${qs.stringify(params)}`}
          >
            <Cross />
          </RemoveShipToLink>
        )}
      </TileRow>
      <TileColumn>
        <NameAndIconContainer>
          <CustomerName data-type="customer-name">
            {customer.address.name}
          </CustomerName>
          {displaySMHLabel && (
            <SMHIconContainer data-type="smh-users" title="SMH User">
              <SMHIcon />
            </SMHIconContainer>
          )}
          {isVmiSite === 'Y' && (
            <VmiIconContainer>
              <VmiSite />
            </VmiIconContainer>
          )}
          {isVmiSite === 'N' && (
            <VmiIconContainer>
              <NonVmiSite />
            </VmiIconContainer>
          )}
        </NameAndIconContainer>
        <CompanyAddress customer={customer} />
      </TileColumn>
    </StyledCustomerTile>
  );
};

export const CustomerTiles: FC<{
  soldTo: ISoldTo;
  shipTo?: IShipTo;
  showRemoveLink?: boolean;
  isVmiSite?: string;
}> = ({ soldTo, shipTo, showRemoveLink = true, isVmiSite }) => {
  return (
    <CustomerTilesContainer>
      <CustomerTile customer={soldTo} type="Sold-To" />
      {shipTo && (
        <>
          <ArrowContainer>
            <Arrow />
          </ArrowContainer>
          <CustomerTile
            customer={shipTo}
            type="Ship-To"
            showRemoveLink={showRemoveLink}
            displaySMHLabel={shipTo.smhActiveUser}
            isVmiSite={isVmiSite}
          />
        </>
      )}
    </CustomerTilesContainer>
  );
};
